import { type ColumnDef } from "components/table";

import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { InfoTooltip } from "components/InfoTooltip";
import { Badge, type BadgeVariant } from "components/ui/badge";
import { TFunction } from "i18next";
import { SearchTerm } from "integrations/firebase/collections/searchTerms";
import { Text } from "components/text";

type MovementIndicator = "up" | "down" | "no_change" | "new";

const INDICATOR_TO_VARIANT: Record<MovementIndicator, BadgeVariant> = {
  up: "trendUp",
  down: "trendDown",
  no_change: "trendNoChange",
  new: "trendNew",
};

const INDICATOR_TO_ICON: Record<MovementIndicator, string> = {
  up: "↑",
  down: "↓",
  no_change: "–",
  new: "–",
};

export const getColumns = (t: TFunction): ColumnDef<SearchTerm>[] => [
  {
    accessorKey: "current_rank",
    minSize: 50,
    size: 50,
    header: ({ column }: { column: any }) => (
      <DataTableColumnHeader
        column={column}
        title={t("searchTerms:table.headers.position")}
      />
    ),
    cell: ({ row }: { row: any }) => (
      <Text as="div" variant="label">
        {row.getValue("current_rank")}
      </Text>
    ),
  },
  {
    accessorKey: "term",
    size: 100,
    header: ({ column }: { column: any }) => (
      <DataTableColumnHeader
        column={column}
        title={t("searchTerms:table.headers.name")}
      />
    ),
    cell: ({ row }: { row: any }) => {
      const params = new URLSearchParams({
        q: row.getValue("term"),
        type: "semantic",
        pageSize: "50",
      });

      return (
        <a
          href={`/books?${params.toString()}`}
          onClick={(event) => event.preventDefault()}
          className="text-secondary-900 truncate visited:text-secondary-600"
        >
          {row.getValue("term")}
        </a>
      );
    },
  },
  {
    accessorKey: "frequency_rank",
    size: 50,
    header: ({ column }: { column: any }) => (
      <div className="inline-flex items-center gap-2">
        <DataTableColumnHeader
          column={column}
          title={t("searchTerms:table.headers.searchFrequencyRank")}
        />
        <InfoTooltip
          side="top"
          text={t("searchTerms:table.tooltips.searchFrequencyRank")}
        />
      </div>
    ),
    cell: ({ row }: { row: any }) => (
      <Text as="div" variant="body">
        {row.getValue("frequency_rank")}
      </Text>
    ),
  },
  {
    accessorKey: "movements_indicator",
    size: 100,
    header: ({ column }: { column: any }) => (
      <div className="inline-flex items-center gap-2">
        <DataTableColumnHeader
          column={column}
          title={t("searchTerms:table.headers.trends")}
        />
        <InfoTooltip side="top" text={t("searchTerms:table.tooltips.trends")} />
      </div>
    ),
    cell: ({ row }: { row: any }) => {
      const indicator = row.getValue(
        "movements_indicator",
      ) as MovementIndicator;
      const deltaRaw: number | null | undefined = row.original.movement_delta;
      const isNoChange = indicator === "no_change";
      const isNew = indicator === "new";
      const delta: number = isNoChange ? 0 : Math.abs(deltaRaw ?? 0);

      return (
        <div>
          {isNew ? (
            <Badge
              variant={INDICATOR_TO_VARIANT.new}
              className="min-w-0 px-2 py-0.5"
            >
              {t("searchTerms:table.headers.new")}
            </Badge>
          ) : (
            <Badge
              variant={INDICATOR_TO_VARIANT[indicator]}
              className="min-w-0 px-2 py-0.5 gap-1"
            >
              {isNoChange ? 0 : delta} {INDICATOR_TO_ICON[indicator]}
            </Badge>
          )}
        </div>
      );
    },
  },
];
